import React, { useState, useEffect } from 'react';
import { Users, Activity, Lock, GraduationCap, Microscope, Calendar, Target, AlertTriangle, CheckCircle2, TrendingUp, Search } from 'lucide-react';
import { GoalMode, AudienceType, Environment, ConnectionConfig } from '../types';
import EmptyState from '../components/EmptyState';

interface DashboardProps {
  goalMode: GoalMode;
  audienceMode: AudienceType;
  activeEnv: Environment | null;
}

interface Analytics {
  users: number;
  sessions: number;
  pageviews: number;
  conversions: number;
  engagementRate: number;
  bounceRate: number;
}

interface SyncResult {
  timestamp: string;
  sources: {
    ga4?: {
      success?: boolean;
      data?: Analytics;
      error?: string;
      setupRequired?: boolean;
      instructions?: string[];
    };
  };
}

const Dashboard: React.FC<DashboardProps> = ({ goalMode, audienceMode, activeEnv }) => {
  const [syncData, setSyncData] = useState<SyncResult | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [seoInsights, setSeoInsights] = useState<any[]>([]);

  const isSample = activeEnv?.isSample || false;
  const hasAnyData = activeEnv ? (Object.values(activeEnv.connections) as ConnectionConfig[]).some(v => v.connected) : false;
  const hasGA4 = activeEnv?.connections.ga4?.connected;

  const handleSync = async () => {
    if (!activeEnv || isSample) return;

    if (!activeEnv) return;

    setSyncing(true);
    try {
      const response = await fetch('/api/sync', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environmentId: activeEnv.id })
      });

      if (response.ok) {
        const data = await response.json();
        setSyncData(data);

        // Also fetch SEO insights
        try {
          const seoResponse = await fetch('/api/seo/research', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'keywords',
              topic: 'laser energetics'
            })
          });

          if (seoResponse.ok) {
            const seoData = await seoResponse.json();
            setSeoInsights((seoData.data || []).slice(0, 5)); // Top 5 keywords
          }
        } catch (err) {
          console.error('SEO fetch error:', err);
        }
      } else {
        console.error('Sync failed');
      }
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  // Auto-sync on mount if connections exist
  useEffect(() => {
    if (hasGA4 && !isSample && activeEnv) {
      handleSync();
    }
  }, [activeEnv?.id, hasGA4, isSample]);

  if (!activeEnv) return <EmptyState />;

  // Get real data or fallback to sample
  const analytics = syncData?.sources?.ga4?.data;
  const showData = isSample || hasAnyData;

  const stats = [
    {
      label: 'Active Users',
      value: analytics?.users ? analytics.users.toLocaleString() : (showData ? '12,400' : '—'),
      change: '+12%',
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      realData: !!analytics?.users
    },
    {
      label: 'Sessions',
      value: analytics?.sessions ? analytics.sessions.toLocaleString() : (showData ? '432' : '—'),
      change: '+8%',
      icon: GraduationCap,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      realData: !!analytics?.sessions
    },
    {
      label: 'Page Views',
      value: analytics?.pageviews ? analytics.pageviews.toLocaleString() : (showData ? '28,450' : '—'),
      change: '+15%',
      icon: Microscope,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      realData: !!analytics?.pageviews
    },
    {
      label: 'Conversions',
      value: analytics?.conversions ? analytics.conversions.toLocaleString() : (showData ? '14' : '—'),
      change: '+2%',
      icon: Calendar,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      realData: !!analytics?.conversions
    },
  ];

  const ga4SetupRequired = syncData?.sources?.ga4?.setupRequired;
  const ga4Error = syncData?.sources?.ga4?.error;

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Intelligence Dashboard</h2>
          <p className="text-slate-500 mt-2 text-sm">
            Analysis for <span className="font-bold text-[#003B71]">{goalMode}</span> targeting <span className="font-bold text-[#003B71]">{audienceMode}</span>.
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing || isSample || !hasGA4}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#0B2341] text-white rounded-xl font-bold text-sm shadow-lg hover:bg-[#0B2341]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {syncing ? 'Syncing...' : 'Sync Node'} <Activity size={16} className={syncing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Connection Status Messages */}
      {!showData && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-center gap-4 text-amber-800">
          <Lock size={20} className="flex-shrink-0 text-amber-500" />
          <div className="text-sm">
            <p className="font-bold">Dashboard Locked.</p>
            <p className="opacity-80">Connect a data source in Settings to unlock analytics.</p>
          </div>
        </div>
      )}

      {ga4SetupRequired && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
            <div>
              <h4 className="font-bold text-blue-900 mb-2">GA4 API Access Required</h4>
              <p className="text-sm text-blue-800 mb-3">
                Your GA4 Measurement ID is connected, but fetching data requires additional setup:
              </p>
              <ol className="text-sm text-blue-800 space-y-1 mb-3">
                {syncData?.sources?.ga4?.instructions?.map((instruction, idx) => (
                  <li key={idx}>{instruction}</li>
                ))}
              </ol>
              <p className="text-xs text-blue-700 italic">
                Until API access is configured, you'll see sample data. This is normal for new setups.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 ${!showData ? 'opacity-40 grayscale pointer-events-none' : ''}`}>
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative">
            {stat.realData && (
              <div className="absolute top-2 right-2">
                <CheckCircle2 size={14} className="text-green-500" title="Real data from GA4" />
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <div className={`p-2 rounded-lg ${stat.bg} ${stat.color}`}>
                <stat.icon size={20} />
              </div>
              <span className="text-[10px] font-black text-green-600 bg-green-50 px-2 py-1 rounded-full">{stat.change}</span>
            </div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{stat.label}</p>
            <h3 className="text-2xl font-black text-slate-900">{stat.value}</h3>
            {!stat.realData && showData && (
              <p className="text-[9px] text-slate-400 mt-1 italic">Sample data</p>
            )}
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 ${!showData ? 'opacity-40 grayscale pointer-events-none' : ''}`}>
        <div className="lg:col-span-2 space-y-6">
          {/* Funnel Health */}
          <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <h3 className="font-black text-lg text-slate-900">Funnel Health Distribution</h3>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Aggregate View</span>
            </div>
            <div className="p-8">
              <div className="space-y-6">
                {[
                  { stage: 'Awareness', pct: 72 },
                  { stage: 'Consideration', pct: 18 },
                  { stage: 'Decision', pct: 10 }
                ].map((item) => (
                  <div key={item.stage}>
                    <div className="flex justify-between text-xs font-bold mb-2">
                      <span>{item.stage}</span>
                      <span className="text-slate-400">{item.pct}%</span>
                    </div>
                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                      <div className="bg-[#003B71] h-full rounded-full transition-all duration-1000" style={{ width: `${item.pct}%` }}></div>
                    </div>
                  </div>
                ))}
                <div className="text-[11px] text-amber-600 font-bold bg-amber-50 p-4 rounded-xl flex gap-3 border border-amber-100">
                  <Target size={16} className="flex-shrink-0" />
                  <span>Strategy Note: Awareness for {audienceMode} is strong.</span>
                </div>
              </div>
            </div>
          </section>

          {/* SEO Insights */}
          {seoInsights.length > 0 && (
            <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <h3 className="font-black text-lg text-slate-900 flex items-center gap-2">
                  <Search size={18} className="text-[#003B71]" />
                  Top SEO Opportunities
                </h3>
              </div>
              <div className="p-6 divide-y divide-slate-100">
                {seoInsights.map((keyword, idx) => (
                  <div key={idx} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-sm text-slate-900">{keyword.keyword}</h4>
                      <span className={`text-xs px-2 py-1 rounded font-bold ${
                        keyword.difficulty < 30 ? 'bg-green-100 text-green-700' :
                        keyword.difficulty < 60 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        Difficulty: {keyword.difficulty}/100
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span><TrendingUp size={12} className="inline mr-1" />{keyword.searchVolume.toLocaleString()}/mo</span>
                      <span className="uppercase">{keyword.intent}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-2">{keyword.recommendation}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {analytics && (
            <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <CheckCircle2 size={18} className="text-green-500" />
                Real-Time Metrics
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Engagement Rate:</span>
                  <span className="font-bold">{(analytics.engagementRate * 100).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Bounce Rate:</span>
                  <span className="font-bold">{(analytics.bounceRate * 100).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Pages/Session:</span>
                  <span className="font-bold">{analytics.sessions > 0 ? (analytics.pageviews / analytics.sessions).toFixed(2) : '0'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Conversion Rate:</span>
                  <span className="font-bold">{analytics.sessions > 0 ? ((analytics.conversions / analytics.sessions) * 100).toFixed(2) : '0'}%</span>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-4 italic">
                Last synced: {new Date(syncData!.timestamp).toLocaleString()}
              </p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
